import { useLayoutEffect, useRef, useState, type ReactNode, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";

export interface HeaderMenuProps {
  id: string;
  label: string;
  children: ReactNode;
  trigger?: ReactNode;
  className?: string;
  activeMenu: string | null;
  setActiveMenu: (id: string | null) => void;
  topLevel?: boolean;
}

/** Fixed, portalled menus do not get clipped by the two horizontal header scrollers. */
export function HeaderMenu({ id, label, children, trigger, className = "", activeMenu, setActiveMenu, topLevel = false }: HeaderMenuProps) {
  const button = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const focusOnOpen = useRef<"first" | "last" | null>(null);
  const typed = useRef({ value: "", at: 0 });
  const [position, setPosition] = useState({ top: 0, left: 8, maxHeight: 400 });
  const open = activeMenu === id;
  const choices = () => Array.from(popover.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
  const close = (restore = false) => {
    setActiveMenu(null);
    if (restore) button.current?.focus({ preventScroll: true });
  };
  const show = (focus: "first" | "last" | null = null) => {
    focusOnOpen.current = focus;
    if (open && focus) (focus === "first" ? choices()[0] : choices().at(-1))?.focus();
    else setActiveMenu(id);
  };
  const switchTopMenu = (key: string) => {
    const triggers = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-top-header-menu]'));
    const index = triggers.indexOf(button.current!);
    const next = key === "Home" ? triggers[0] : key === "End" ? triggers.at(-1) : triggers[(index + (key === "ArrowRight" ? 1 : -1) + triggers.length) % triggers.length];
    if (next) {
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: "nearest", inline: "nearest" });
      if (open) next.dispatchEvent(new window.KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true }));
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    typed.current = { value: "", at: 0 };
    const place = () => {
      const rect = button.current?.getBoundingClientRect();
      if (!rect) return;
      const width = popover.current?.getBoundingClientRect().width ?? 245;
      setPosition({ top: rect.bottom + 3, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), maxHeight: Math.max(0, window.innerHeight - rect.bottom - 11) });
    };
    place();
    const target = focusOnOpen.current;
    if (target) (target === "last" ? choices().at(-1) : choices()[0])?.focus({ preventScroll: true });
    focusOnOpen.current = null;
    const outside = (event: Event) => {
      if (!popover.current?.contains(event.target as Node) && !button.current?.contains(event.target as Node)) setActiveMenu(null);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    window.addEventListener("resize", place);
    document.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
    };
  }, [open, setActiveMenu]);

  const keys = (event: KeyboardEvent, inMenu: boolean) => {
    const { key } = event;
    if (key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); close(true); return; }
    if (key === "Tab") { if (open) close(inMenu); return; }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (topLevel && (key === "ArrowLeft" || key === "ArrowRight" || (!inMenu && ["Home", "End"].includes(key)))) {
      event.preventDefault(); event.stopPropagation(); switchTopMenu(key); return;
    }
    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault(); event.stopPropagation();
      if (!inMenu) show(key === "ArrowUp" ? "last" : "first");
      else {
        const items = choices();
        const index = items.indexOf(document.activeElement as HTMLElement);
        items[(index + (key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
      }
    } else if (inMenu && (key === "Home" || key === "End")) {
      event.preventDefault(); event.stopPropagation();
      (key === "Home" ? choices()[0] : choices().at(-1))?.focus();
    } else if (inMenu && key.length === 1 && key !== " ") {
      event.preventDefault(); event.stopPropagation();
      const now = Date.now();
      typed.current = { value: now - typed.current.at < 700 ? typed.current.value + key.toLowerCase() : key.toLowerCase(), at: now };
      const items = choices(), index = items.indexOf(document.activeElement as HTMLElement);
      const ordered = [...items.slice(index + 1), ...items.slice(0, index + 1)];
      (ordered.find(item => item.textContent?.trim().toLowerCase().startsWith(typed.current.value)) ?? ordered.find(item => item.textContent?.trim().toLowerCase().startsWith(key.toLowerCase())))?.focus();
    }
  };

  return <>
    <button ref={button} id={`${id}-trigger`} type="button" className={`header-menu-trigger ${className} ${open ? "active" : ""}`}
      data-top-header-menu={topLevel ? id : undefined} aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? `${id}-menu` : undefined}
      onClick={(event) => open ? close() : show(event.detail === 0 ? "first" : null)} onKeyDown={(event) => keys(event, false)}
      onPointerEnter={(event) => { if (event.pointerType === "mouse" && topLevel && activeMenu?.startsWith("top-")) show(); }}>
      {trigger ?? label}
    </button>
    {open ? createPortal(<div ref={popover} id={`${id}-menu`} className="header-popover" role="menu" aria-labelledby={`${id}-trigger`} style={position}
      onKeyDown={(event) => keys(event, true)} onClick={(event) => { if ((event.target as Element).closest('[role="menuitem"]:not(:disabled)')) close(true); }}>
      {children}
    </div>, document.body) : null}
  </>;
}

export function MenuSeparator() { return <div className="header-menu-separator" role="separator" />; }
